import React, {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useRef,
} from 'react';
import { monitoring } from '../monitoring/monitoring';

export interface MonitoringContextValue {
  reportAppLoaded: () => void;
  startCategorySwitchInteraction: () => void;
  stopActiveInteraction: () => void;
}

interface MonitoringProviderProps {}

const MonitoringContext = createContext<MonitoringContextValue>({
  reportAppLoaded: () => {},
  startCategorySwitchInteraction: () => {},
  stopActiveInteraction: () => {},
});

export const MonitoringProvider: FC<
  PropsWithChildren<MonitoringProviderProps>
> = ({ children }) => {
  const activeInteraction = useRef<'category-switch' | null>(null);
  const reportAppLoaded = useCallback(() => {
    monitoring.appLoaded();
  }, []);

  const startCategorySwitchInteraction = useCallback(() => {
    activeInteraction.current = 'category-switch';
    monitoring.interactionStarted('category-switch');
  }, []);

  const stopActiveInteraction = useCallback(() => {
    if (activeInteraction.current) {
      monitoring.interactionEnded(activeInteraction.current);
      activeInteraction.current = null;
    }
  }, []);

  return (
    <MonitoringContext.Provider
      value={{
        reportAppLoaded,
        startCategorySwitchInteraction,
        stopActiveInteraction,
      }}
    >
      {children}
    </MonitoringContext.Provider>
  );
};

export const useMonitoring = () => {
  const context = React.useContext(MonitoringContext);
  if (!context) {
    throw new Error('useMonitoring must be used within a MonitoringProvider');
  }

  return context;
};
